import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BACKEND_URL } from "../common/Common";
import useEmirateFromCoordinates from "../common/Common";
import axios from "axios";
import "../experiencelist/ExperienceList.css";
import "../categorylist/CategoryList.css";
import "../nearyou/NearYou.css";
import Navbar from "../common-component/navbar/Navbar";
import leftarrow from '../../assets/offers/leftarrow.png';
import search from '../../assets/home-images/search.png';
import flagicon from '../../assets/home-images/flagicon.png';
import businessicon from '../../assets/home-images/businessicon.jpg';
import expbanner from '../../assets/experience/Expbanner.png';
import nodata from '../../assets/experience/nodata.png';
import xmark from '../../assets/home-images/right-section-images/xmark.png';
import downarrow from '../../assets/home-images/downarrow.png';

const ExperienceList = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [countrypopup, setCountrypopup] = useState(false);
    const [states, setStates] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [latitude, setLatitude] = useState(24.4539);
    const [longitude, setLongitude] = useState(54.3773);
    const emirate = useEmirateFromCoordinates(latitude, longitude);
    const [hotelLuxuryData, setHotelLuxuryData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sectionTitle, setSectionTitle] = useState("Hotels & Luxury Retreats");
    const [sectionSubtitle, setSectionSubtitle] = useState("Refined stays designed for rest, indulgence, and memorable moments.");

    // Load selected state from localStorage on mount
    useEffect(() => {
        const savedState = localStorage.getItem('selectedState');
        if (savedState) {
            try {
                const parsedState = JSON.parse(savedState);
                setSelectedState(parsedState);
                const lat = parseFloat(parsedState.latitude || parsedState.lat || 24.4539);
                const lng = parseFloat(parsedState.longitude || parsedState.lng || 54.3773);
                setLatitude(lat);
                setLongitude(lng);
            } catch (error) {
                console.error("Error parsing saved state:", error);
            }
        }
    }, []);

    useEffect(() => {
        // Only use geolocation if no saved state exists
        if (!localStorage.getItem('selectedState') && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLatitude(position.coords.latitude);
                    setLongitude(position.coords.longitude);
                },
                (error) => {
                    console.error("Error getting location:", error);
                }
            );
        }
    }, []);

    // Handle location change from country popup
    const handleStateChange = useCallback((state) => {
        const lat = parseFloat(state.latitude || state.lat || 24.4539);
        const lng = parseFloat(state.longitude || state.lng || 54.3773);

        console.log("🟢 Location Selected:", state.name, "Lat:", lat, "Lng:", lng);

        setSelectedState(state);
        setLatitude(lat);
        setLongitude(lng);
        setCountrypopup(false);

        localStorage.setItem('selectedState', JSON.stringify(state));
    }, []);

    // Fetch master data for states
    const fetchMasterData = async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}website/get-master-data`);
            if (response.data.stateData) {
                setStates(response.data.stateData);
            } else {
                setStates([]);
            }
        } catch (error) {
            console.error("Error fetching master data:", error);
        }
    };

    // Fetch hotel/luxury data
    const fetchHotelLuxuryData = useCallback(async (search = "") => {
        if (!latitude || !longitude) return;

        setIsLoading(true);

        const payload = {
            latitude: latitude,
            longitude: longitude,
            state_id: selectedState?.id || emirate?.id || "",
            user_id: "",
        };

        try {
            const response = await axios.post(
                `${BACKEND_URL}website/getofferspagelist?_t=${Date.now()}`,
                payload,
                {
                    timeout: 10000,
                    withCredentials: false,
                    headers: {
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        "Pragma": "no-cache",
                    },
                }
            );

            // Filter for view_type === 6 (Hotel & Luxury)
            const allOffers = response.data || [];
            const hotelLuxuryOffers = Array.isArray(allOffers) 
                ? allOffers.filter(offer => offer.view_type === 6)
                : [];

            // If ID is provided, filter to show only that specific section
            let targetSection = null;
            if (id) {
                const sectionId = parseInt(id, 10);
                targetSection = hotelLuxuryOffers.find(section => section.id === sectionId);
            }

            // Extract user_items from the selected section or all sections
            let allUserItems = [];
            let title = "Hotels & Luxury Retreats";
            let subtitle = "Refined stays designed for rest, indulgence, and memorable moments.";

            if (targetSection) {
                // Show only the selected section
                if (targetSection.title) {
                    title = targetSection.title;
                }
                if (targetSection.subtitle) {
                    subtitle = targetSection.subtitle;
                }
                if (targetSection.user_items && Array.isArray(targetSection.user_items)) {
                    // Filter by search term if provided
                    const filteredItems = search.trim() 
                        ? targetSection.user_items.filter(item => 
                            (item.user_name && item.user_name.toLowerCase().includes(search.toLowerCase())) ||
                            (item.location && item.location.toLowerCase().includes(search.toLowerCase()))
                          )
                        : targetSection.user_items;
                    allUserItems = filteredItems;
                }
            } else {
                // If no ID provided, show all sections (original behavior)
                hotelLuxuryOffers.forEach(section => {
                    if (section.title) {
                        title = section.title;
                    }
                    if (section.subtitle) {
                        subtitle = section.subtitle;
                    }
                    if (section.user_items && Array.isArray(section.user_items)) {
                        // Filter by search term if provided
                        const filteredItems = search.trim() 
                            ? section.user_items.filter(item => 
                                (item.user_name && item.user_name.toLowerCase().includes(search.toLowerCase())) ||
                                (item.location && item.location.toLowerCase().includes(search.toLowerCase()))
                              )
                            : section.user_items;
                        allUserItems = [...allUserItems, ...filteredItems];
                    }
                });
            }

            setHotelLuxuryData(allUserItems);
            setSectionTitle(title);
            setSectionSubtitle(subtitle);
        } catch (error) {
            console.error("❌ Error fetching Hotel/Luxury data:", error);
            setHotelLuxuryData([]);
        } finally {
            setIsLoading(false);
        }
    }, [latitude, longitude, selectedState, emirate, id]);

    // Initial data fetch
    useEffect(() => {
        fetchMasterData();
    }, []);

    // Fetch hotel/luxury data when location or ID changes
    useEffect(() => {
        fetchHotelLuxuryData(searchTerm);
    }, [latitude, longitude, fetchHotelLuxuryData, id]);

    // Debounced search effect - only triggers when searchTerm changes
    useEffect(() => {
        if (!latitude || !longitude) return;

        // Debounce the API call
        const timeoutId = setTimeout(() => {
            setIsLoading(true);
            fetchHotelLuxuryData(searchTerm);
        }, 500); // 500ms debounce delay

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    return (
        <div>
            <Navbar
                selectedState={selectedState}
                onStateChange={handleStateChange}
                states={states}
                showLocationSelector={true}
            />

            {/* Mobile Header - Below Navbar */}
            <div className="mobile-category-header">
                <div className="mobile-header-top">
                    <div className="mobile-category-title">
                        <img src={leftarrow} alt="back" className="mobile-back-icon" onClick={() => navigate(-1)} />
                        <h4>{sectionTitle}</h4>
                    </div>
                </div>
                <div className="mobile-search-filter-container">
                    <div className="mobile-searchbox">
                        <img src={search} alt="search" />
                        <input 
                            type="text" 
                            placeholder="Search..." 
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>
            </div>

            <div className="experience-main-layout category-list-page-main-layout">
                <aside className="left-section">
                    <div className="searchbox">
                        <img src={search} alt="search" />
                        <input 
                            type="text" 
                            placeholder="Search..." 
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="left-menu-content">
                        <div className="img-contentname" onClick={() => setCountrypopup(true)}>
                            <img src={flagicon} alt="flag" className="flagicon" />
                            <p>{selectedState?.name || emirate?.name || "Abu Dhabi"}</p>
                        </div>
                    </div>
                    <div className="divider-line"></div>
                    <div className="left-bottom-links">
                        <div className="left-bottom1">
                            <div className="content-arrow">
                                <Link to="/merchantagreement" className="privacy-link">
                                    <span>Privacy & Terms</span>
                                    <img src={downarrow} alt="down arrow" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </aside>

                <main className="experience-middle-section">
                    <div className="mid-section-head-div">
                        <img src={expbanner} alt="enjoy summer" className="enjoy-summer-img" />
                    </div>

                    <div className="category-title catgeory-list-page experiencelistpage">
                        {isLoading ? (
                            <div className="shimmer-title-container">
                                <div className="shimmer-title-breadcrumb"></div>
                                <div className="shimmer-title-text"></div>
                            </div>
                        ) : (
                            <h6 className="viewtype-6-title">
                                <Link to="/experience" className="remove-underline">
                                    <span>Experience /</span>
                                </Link>
                                {' '}{sectionTitle}
                            </h6>
                        )}
                        <p></p>
                    </div>

                    {isLoading ? (
                        <div className="hotel-luxury-subtitle">
                            <div className="shimmer-subtitle-line shimmer-subtitle-line-1"></div>
                            <div className="shimmer-subtitle-line shimmer-subtitle-line-2"></div>
                        </div>
                    ) : (
                        sectionSubtitle && (
                            <div className="hotel-luxury-subtitle">
                                <p>{sectionSubtitle}</p>
                            </div>
                        )
                    )}

                    <div className="experience-list-body">
                        <div className="experience-list-grid-container">
                        {isLoading ? (
                            // Shimmer loading skeleton
                            <div className="shimmer-hotel-container">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={`shimmer-${index}`} className="group-hotelimg-hotellogo-hoteldetails shimmer-hotel-card">
                                        <div className="hotel-images shimmer-hotel-image"></div>
                                        <div className="hotel-logo shimmer-hotel-logo"></div>
                                        <div className="hotel-details shimmer-hotel-details">
                                            <div className="shimmer-hotel-title"></div>
                                            <div className="shimmer-hotel-location"></div>
                                        </div>
                                        {/* Mobile shimmer layout */}
                                        <div className="mobile-hotel-item shimmer-mobile-hotel-item">
                                            <div className="mobile-hotel-logo shimmer-mobile-hotel-logo"></div>
                                            <div className="mobile-hotel-text shimmer-mobile-hotel-text">
                                                <div className="shimmer-hotel-title"></div>
                                                <div className="shimmer-hotel-location"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : hotelLuxuryData.length > 0 ? (
                            hotelLuxuryData.map((item, index) => {
                                if (!item) return null;

                                return (
                                    <Link
                                        to={`/vendordetails/${item.user_id}`}
                                        className="remove-underline group-hotelimg-hotellogo-hoteldetails"
                                        key={`${item.user_id}-${index}`}
                                    >
                                        {/* Desktop layout */}
                                        <div className="desktop-hotel-link">
                                            <img
                                                src={item.user_image || ""}
                                                className="hotel-images"
                                                alt={item.user_name || "Hotel"}
                                                loading={index < 12 ? "eager" : "lazy"}
                                                decoding="async"
                                                onError={(e) => {
                                                    e.target.src = "/no-image.png";
                                                }}
                                            />
                                            <img
                                                src={item.user_image || businessicon}
                                                className="hotel-logo"
                                                alt={item.user_name || "Hotel logo"}
                                                loading={index < 12 ? "eager" : "lazy"}
                                                decoding="async"
                                                onError={(e) => {
                                                    e.target.src = businessicon;
                                                }}
                                            />
                                        </div>

                                        <div className="hotel-details">
                                            <div className="hotel-title-row">
                                                <div className="hotel-title-link">
                                                    <h5>{item.user_name || "Untitled"}</h5>
                                                </div>
                                            </div>
                                            <p>{item.distance ? item.distance.toFixed(1) : '0.0'} km <span className="dividerforkm-city-experiencelist">|</span> {item.location || "N/A"}</p>
                                        </div>

                                        {/* Mobile layout */}
                                        <div className="mobile-hotel-item">
                                            <img
                                                src={item.user_image || businessicon}
                                                className="mobile-hotel-logo"
                                                alt={item.user_name || "Hotel logo"}
                                                loading={index < 12 ? "eager" : "lazy"}
                                                decoding="async"
                                                onError={(e) => {
                                                    e.target.src = businessicon;
                                                }}
                                            />
                                            <div className="mobile-hotel-text">
                                                <h5>{item.user_name || "Untitled"}</h5>
                                                <p>{item.distance ? item.distance.toFixed(1) : '0.0'} km <span className="dividerforkm-city-experiencelist">|</span> {item.location || "N/A"}</p>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })
                        ) : (
                            <div className="nodata-message-categorylist">
                                <img src={nodata} alt="no data" className="no-data-img" />
                                <p className="no-data-p1">No hotels found</p>
                                <p className="no-data-p2">Try adjusting your search</p>
                            </div>
                        )}
                        </div>
                    </div>
                </main>

                {/* Country pop up */}
                {countrypopup && (
                    <>
                        {/* Overlay */}
                        <div className="custom-overlay" onClick={() => setCountrypopup(false)}></div>

                        {/* Centered Popup */}
                        <div className="custom-popup">
                            <div className="custom-popup-header">
                                <h4>Choose Your Location</h4>
                                <img src={xmark} alt="close" className="close-popup" onClick={() => setCountrypopup(false)} />
                            </div>

                            {/* States List */}
                            <div className="custom-popup-body">
                                <div className="state-grid">
                                    {states.filter((state) => Number(state.country_id))
                                        .map((state, index, arr) => {
                                            const isDisabled = index >= arr.length - 4; // last 4 items

                                            return (
                                                <div
                                                    key={state.id}
                                                    className={`state-item ${isDisabled ? "disabled" : ""}`}
                                                    onClick={() => !isDisabled && handleStateChange(state)}
                                                >
                                                    <img
                                                        src={flagicon}
                                                        alt="flag"
                                                        className="flagicon-popup"
                                                    />
                                                    <p>{state.name}</p>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ExperienceList;
